import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import type { AttachmentDto, CreateAttachmentDto, UpdateAttachmentDto } from '../attachments/models';
import type { IFormFile } from '../microsoft/asp-net-core/http/models';
import type { DataRequestDto, EntitySchemaDto, IdNameDto, PageRequestDto } from '../models';

@Injectable({
  providedIn: 'root',
})
export class AttachmentService {
  apiName = 'Default';
  

  copyAttachments = (fromEntityTypeFullName: string, fromEntityRecordId: string, toEntityTypeFullName: string, toEntityRecordId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: '/api/app/attachment/copy-attachments',
      params: { fromEntityTypeFullName, fromEntityRecordId, toEntityTypeFullName, toEntityRecordId },
    },
    { apiName: this.apiName,...config });
  

  create = (input: CreateAttachmentDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, AttachmentDto>({
      method: 'POST',
      url: '/api/app/attachment',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createOrReplace = (input: CreateAttachmentDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, AttachmentDto>({
      method: 'POST',
      url: '/api/app/attachment/or-replace',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/attachment/${id}`,
    },
    { apiName: this.apiName,...config });
  

  deleteByRecordId = (entityRecordId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/attachment/by-record-id/${entityRecordId}`,
    },
    { apiName: this.apiName,...config });
  

  download = (id: string, path: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, AttachmentDto>({
      method: 'POST',
      url: `/api/app/attachment/${id}/download`,
      params: { path },
    },
    { apiName: this.apiName,...config });
  

  downloadById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, AttachmentDto>({
      method: 'POST',
      url: `/api/app/attachment/${id}/download-by-id`,
    },
    { apiName: this.apiName,...config });
  

  downloadByRecordId = (entityRecordId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, AttachmentDto>({
      method: 'POST',
      url: `/api/app/attachment/download-by-record-id/${entityRecordId}`,
    },
    { apiName: this.apiName,...config });
  

  downloadFile = (entity: string, fileName: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number[]>({
      method: 'POST',
      url: '/api/app/attachment/download-file',
      params: { entity, fileName },
    },
    { apiName: this.apiName,...config });
  

  get = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, AttachmentDto>({
      method: 'GET',
      url: `/api/app/attachment/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getByRecordId = (entityRecordId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, AttachmentDto>({
      method: 'GET',
      url: `/api/app/attachment/by-record-id/${entityRecordId}`,
    },
    { apiName: this.apiName,...config });
  

  getCount = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'GET',
      url: '/api/app/attachment/count',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getDropDownOptions = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<IdNameDto>>({
      method: 'GET',
      url: '/api/app/attachment/drop-down-options',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getExpandById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, AttachmentDto[]>({
      method: 'GET',
      url: `/api/app/attachment/${id}/expand`,
    },
    { apiName: this.apiName,...config });
  

  getExtraPropertiesById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Record<string, object>>({
      method: 'GET',
      url: `/api/app/attachment/${id}/extra-properties`,
    },
    { apiName: this.apiName,...config });
  

  getIds = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string[]>({
      method: 'GET',
      url: '/api/app/attachment/ids',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getList = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<AttachmentDto>>({
      method: 'GET',
      url: '/api/app/attachment',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getOneExtraPropertiesByIdAndKey = (id: string, key: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, object>({
      method: 'GET',
      url: `/api/app/attachment/${id}/one-extra-properties`,
      params: { key },
    },
    { apiName: this.apiName,...config });
  

  getSchema = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, EntitySchemaDto>({
      method: 'GET',
      url: '/api/app/attachment/schema',
    },
    { apiName: this.apiName,...config });
  

  getWithDetails = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, AttachmentDto>({
      method: 'GET',
      url: `/api/app/attachment/${id}/with-details`,
    },
    { apiName: this.apiName,...config });
  

  setExtraPropertiesByIdAndKeyAndValue = (id: string, key: string, value: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/attachment/${id}/set-extra-properties`,
      params: { key, value },
    },
    { apiName: this.apiName,...config });
  

  update = (id: string, input: UpdateAttachmentDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, AttachmentDto>({
      method: 'PUT',
      url: `/api/app/attachment/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  upload = (entity: string, fileName: string, file: IFormFile, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string>({
      method: 'POST',
      responseType: 'text',
      url: '/api/app/attachment/upload',
      params: { entity, fileName },
      body: file,
    },
    { apiName: this.apiName,...config });

  constructor(private restService: RestService) {}
}
